import { useEffect, useState } from "react";
import MusicPlayer from "./MusicPlayer";
import { buildSampleSongUrl } from "../utils/general-config";

export const SAMPLE_CATEGORIES = [
  "Anniversary",
  "Mother's Day",
  "Birthday",
  "Thank You",
  "Missing You",
];

export const SAMPLE_GENRES = ["Country", "Acoustic", "R&B"];

export const GENRE_SLUG_MAP: Record<string, string> = {
  "R&B": "rnb",
  "Country": "country",
  "Acoustic": "acoustic",
};

export const OCCASION_SLUG_MAP: Record<string, string> = {
  "Anniversary": "anniversary",
  "Mother's Day": "mothers-day",
  "Birthday": "birthday",
  "Thank You": "thank-you",
  "Missing You": "missing-you",
};

export function buildSampleSongFileName(occasion: string): string {
  const slug = OCCASION_SLUG_MAP[occasion];
  if (!slug) {
    console.warn(`No slug mapping found for occasion: "${occasion}".`);
    return "";
  }
  return `${slug}.mp3`;
}

export function getSampleSongAudioUrl(occasion: string, genre: string): string {
  const genreSlug = GENRE_SLUG_MAP[genre] ?? genre.toLowerCase();
  const filename = buildSampleSongFileName(occasion);
  return buildSampleSongUrl(genreSlug, filename);
}

/** Warm browser / CDN cache in the background — no UI loader. */
function prefetchSampleSongs(): void {
  for (const occasion of SAMPLE_CATEGORIES) {
    for (const genre of SAMPLE_GENRES) {
      const url = getSampleSongAudioUrl(occasion, genre);
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.as = "fetch";
      link.href = url;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    }
  }
}

export async function fetchSampleSongBlob(
  occasion: string,
  genre: string,
): Promise<{ blob: Blob; blobUrl: string }> {
  const url = getSampleSongAudioUrl(occasion, genre);
  const response = await fetch(url, { cache: "force-cache" });
  if (!response.ok) {
    throw new Error(`Failed to fetch sample song from ${url} (${response.status})`);
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  return { blob, blobUrl };
}

export interface HearOtherSongsSectionProps {
  isDark?: boolean;
}

const HearOtherSongsSection = ({
  isDark = false,
}: HearOtherSongsSectionProps) => {
  const [activeCategory, setActiveCategory] = useState<string>(SAMPLE_CATEGORIES[0]);
  const [selectedGenre, setSelectedGenre] = useState<string>(SAMPLE_GENRES[0]);

  useEffect(() => {
    prefetchSampleSongs();
  }, []);

  const audioUrl = getSampleSongAudioUrl(activeCategory, selectedGenre);
  const trackTitle = activeCategory;

  return (
    <div
      className={`w-full rounded-[10px] border-2 px-4 py-4 sm:px-6 sm:py-5 ${
        isDark ? "border-[#264a73] bg-[#1e3554]" : "border-[#d2e5ff] bg-white"
      }`}
    >
      <p
        className={`mb-4 text-[16px] sm:text-[18px] font-normal ${
          isDark ? "text-[#fdfdfd]" : "text-[#16283d]"
        }`}
      >
        Hear other songs we made
      </p>

      <div className="no-scrollbar mb-4 flex flex-nowrap gap-1 sm:gap-2 overflow-x-scroll">
        {SAMPLE_CATEGORIES.map((cat) => {
          const isActive = cat === activeCategory;
          return (
            <button
              key={cat}
              type="button"
              id={`sample-cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={(e) => {
                setActiveCategory(cat);
                e.currentTarget.scrollIntoView({
                  behavior: "smooth",
                  inline: "nearest",
                  block: "nearest",
                });
              }}
              className={[
                "cursor-pointer whitespace-nowrap rounded-full px-5 py-[5px] sm:px-5 sm:py-2 text-[12px] sm:text-[14px] leading-normal transition-colors border-1 sm:border-2",
                isActive
                  ? isDark
                    ? "border-[#0e76ff] bg-[#264a73] text-[#fdfdfd] font-semibold"
                    : "border-[#0e76ff] bg-[#e7f1ff] text-[#0e76ff] font-semibold"
                  : isDark
                  ? "border-[#3a4a5c] bg-transparent text-[#DBDEE1] font-normal hover:border-[#0e76ff]"
                  : "border-[#E4E7EC] bg-white text-[#344054] font-normal hover:border-[#a6ccff]",
              ].join(" ")}
            >
              {cat}
            </button>
          );
        })}
      </div>

      <MusicPlayer
        key={`${activeCategory}:${selectedGenre}`}
        src={audioUrl}
        title={trackTitle}
        eqValues={[0, 0, 0, 0, 0, 0, 0, 0, 0, 0]}
        playThroughLiveEq={false}
        isDark={isDark}
        showLoadingState={false}
      />

      <div className="flex flex-wrap items-center justify-center gap-2 pt-4">
        {SAMPLE_GENRES.map((g) => {
          const isSelected = selectedGenre === g;
          return (
            <button
              key={g}
              type="button"
              id={`sample-genre-${g.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
              onClick={() => setSelectedGenre(g)}
              className={[
                "cursor-pointer rounded-full px-4 py-1.5 text-[13px] leading-normal transition-colors border",
                isSelected
                  ? isDark
                    ? "border-[#0e76ff] bg-[#264a73] font-bold text-[#fdfdfd]"
                    : "border-[#0e76ff] bg-[#e7f1ff] font-bold text-[#0e76ff]"
                  : isDark
                  ? "border-[#264a73] bg-transparent text-[#DBDEE1] font-normal hover:border-[#0e76ff]"
                  : "border-[#d2e5ff] bg-white text-[#16283d] font-normal hover:border-[#a6ccff]",
              ].join(" ")}
            >
              {g}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default HearOtherSongsSection;
